import { SeamlessCoreStore } from '@seamless/store';
import VueSeamlessStore from '@seamless/vue-store-plugin';
import { Component, Prop, Vue } from 'vue-property-decorator';
import {
  ConnectionAemEnvironmentState,
  addConnectionAemEnvironmentConnection,
  connectionName as aemEnvConnectionName,
} from '@owc/connection-aem-environment';

import { Logger, APP_COMPONENTS } from '@b2x/utils';
import { b2xlcComponentConfig } from './interfaces/types';
import { initializeComponentRegistry } from '@owc/component-registry-connection';
import {
  JwtIssuers,
  Stages,
  addAuthConnection,
  AuthConnection,
  AuthState,
  IntegrationContexts,
  Environment,
  jweIssuerKey,
  AUTHENTICATION_CONNECTION,
  Claims,
} from '@b2x/login-library';

if (!window.aemNamespace) {
  Vue.use(new VueSeamlessStore());
}

@Component({
  mixins: [new VueSeamlessStore().mixin],
})
export default class App extends Vue {
  /**
   * Following two properties are only made for legacy header integration,
   * in Header v2 integration, they are retrieved through @owc/component-registry-connection
   */
  @Prop({ type: String, default: 'Logout' })
  dataLoggedinLabel!: string;

  @Prop({ type: String, default: 'Login' })
  dataLoggedoutLabel!: string;

  // https://pages.git.daimler.com/dh-io-mbmxp/seamless/#/guidelines/store/component-registry?id=component-registry
  @Prop({ type: String })
  componentId!: string;

  /**
   * Since the Awareness Team struggled to pass HEADER_V2 to the attribute integration context, we decided to give
   * this responsibility to the Legacy Header, and set HEADER_V2 as the default integration context.
   */
  @Prop({ type: String })
  environment!: IntegrationContexts;

  /* TODO: Remove when OneFrontend & Legacy integration upgrade to new name (environment) */
  @Prop({ type: String })
  dataIntegrationContext!: IntegrationContexts;

  @Prop({ type: String })
  country!: string;

  @Prop({ type: String })
  language!: string;

  @Prop()
  authority!: JwtIssuers;

  /* TODO: Remove when OneFrontend & Legacy integration upgrade to new name (authority) */
  @Prop()
  context!: JwtIssuers;

  /* TODO: Remove when OneFrontend & Legacy integration upgrade to new name (authority) */
  authorityLocal = this.authority || this.context || JwtIssuers.CIAS;

  /* TODO: Remove when OneFrontend & Legacy integration upgrade to new name (environment) */
  environmentLocal = this.environment || this.dataIntegrationContext || 'HEADER_V2';

  authConnection!: AuthConnection;
  componentRegistry!: any;
  store!: SeamlessCoreStore;
  claims: Claims | null = null;
  scopedAuthConnection!: string;
  env!: Environment;

  translations: Record<string, string> = {
    login: this.dataLoggedoutLabel,
    logout: this.dataLoggedinLabel,
  };

  logger = new Logger('b2xLC', APP_COMPONENTS.B2X_LC, this.authorityLocal);

  async created(): Promise<void> {
    this.logger.log('login component created', this.$props);

    const { connectionName, connection } = await addAuthConnection({
      country: this.country,
      language: this.language,
      authority: this.authorityLocal,
      environment: this.environmentLocal,
    });

    this.scopedAuthConnection = connectionName;
    this.authConnection = connection;

    await addConnectionAemEnvironmentConnection();

    this.store.subscribe(this.scopedAuthConnection, this.onAuthEvent);

    this.store.subscribe(aemEnvConnectionName, this.onAEMEvent);

    this.store.subscribe(AUTHENTICATION_CONNECTION, this.onAuthenticationEvent);

    if (window.top.aemNamespace) {
      this.componentRegistry = initializeComponentRegistry();
      this.componentRegistry.subscribe(this.componentId, this.onComponentConfig);
    }

    // this is where we initialize when in demo env or legacy header
    // for header v2 integration check onAEMEvent() method
    if (this.environmentLocal === 'STANDALONE' || this.environmentLocal === 'LEGACY') {
      // in case init params have been provided via attributes, we pass them along,
      // if we are in legacy header, init params will be detected in Environment
      this.logger.log('Detected LEGACY or STANDALONE, triggering initAuthentication()');
      this.authConnection.initAuthentication();
    }
  }

  onLogin(): void {
    this.logger.log('onLogin called');

    // check for claims is needed as there is only one button, but it shows text for both login and logout actions,
    // We only need to call login action as logout happens in the user menu, not from this button
    if (!this.claims) {
      this.authConnection.authenticate(this.env);
    }
  }

  onLogout(): void {
    this.logger.log('onLogout called');

    this.authConnection.logout(this.env);
  }

  onAuthenticationEvent(defaultState: AuthState): void {
    const defaultIssuer = defaultState.auth.jweIssuer;
    this.logger.log('New default state event, issuer ', defaultIssuer);

    this.claims = this.authorityLocal === defaultIssuer ? defaultState.auth.claims : null;
  }

  onAuthEvent(state: AuthState): void {
    this.logger.log(`New ${this.scopedAuthConnection} event`, state);
    const activeJweIssuer = window.sessionStorage.getItem(jweIssuerKey);

    // only show as logged in, if active issuer matches state issuer
    if (state.auth && state.auth.jweIssuer === activeJweIssuer) {
      this.claims = state.auth.claims;
    }
  }

  onAEMEvent(state: ConnectionAemEnvironmentState): void {
    this.logger.log(`New ${aemEnvConnectionName} event`, state);

    if (state.stage && state.country && state.language) {
      this.logger.log(
        'calling initAuthentication after receiving AEM env data',
        state.stage,
        state.country,
        state.language,
      );

      this.env = new Environment({
        country: state.country,
        language: state.language,
        stage: state.stage.toLowerCase() as Stages,
        authority: this.authorityLocal,
        environment: this.environmentLocal,
      });

      this.authConnection.initAuthentication(this.env);
    }
  }

  onComponentConfig(componentConfig: b2xlcComponentConfig): void {
    this.logger.log('component config', { componentConfig });

    if (componentConfig.payload) {
      this.translations.login = componentConfig.payload.login;
      this.translations.logout = componentConfig.payload.logout;
    }
  }

  beforeDestroy(): void {
    this.store.unsubscribe(this.scopedAuthConnection, this.onAuthEvent);
    this.store.unsubscribe(aemEnvConnectionName, this.onAEMEvent);
    this.store.unsubscribe(this.componentId, this.onComponentConfig);
    this.store.unsubscribe(AUTHENTICATION_CONNECTION, this.onAuthenticationEvent);
  }
}
